import { useEffect, useRef, useState } from 'react';
import { msUntilNextMinute, zonedParts, type ZonedParts } from '@obv/core';

/** Household wall-clock, re-rendering on each minute boundary and on wake from background. */
export function useClock(tz: string): ZonedParts & { now: Date } {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const d = new Date();
      setNow(d);
      timer = setTimeout(tick, msUntilNextMinute(d));
    };
    timer = setTimeout(tick, msUntilNextMinute(new Date()));
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timer);
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  return { ...zonedParts(now, tz), now };
}

export type Orientation = 'landscape' | 'portrait';

export function useOrientation(): Orientation {
  const query = '(orientation: portrait)';
  const get = (): Orientation => (window.matchMedia(query).matches ? 'portrait' : 'landscape');
  const [o, setO] = useState<Orientation>(get);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setO(get());
    // Safari 12 only supports the deprecated addListener API on MediaQueryList.
    mql.addListener(onChange);
    window.addEventListener('resize', onChange);
    return () => {
      mql.removeListener(onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);
  return o;
}

/** True on a phone-sized screen (under 600px wide), where the display stacks into one column. */
export function useIsPhone(): boolean {
  const query = '(max-width: 599px)';
  const get = () => window.matchMedia(query).matches;
  const [phone, setPhone] = useState<boolean>(get);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setPhone(get());
    // Safari 12 only supports the deprecated addListener API on MediaQueryList.
    mql.addListener(onChange);
    window.addEventListener('resize', onChange);
    return () => {
      mql.removeListener(onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);
  return phone;
}

/** Calls `onIdle` once after `ms` without touch/pointer/key input; re-arms on activity. */
export function useIdle(ms: number, onIdle: () => void, enabled = true): void {
  const cb = useRef(onIdle);
  cb.current = onIdle;
  useEffect(() => {
    if (!enabled) return;
    let timer = setTimeout(() => cb.current(), ms);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => cb.current(), ms);
    };
    const events = ['touchstart', 'mousedown', 'keydown', 'wheel'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true } as AddEventListenerOptions));
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [ms, enabled]);
}

/** True when running as a Home Screen web app on iOS. */
export function isStandalone(): boolean {
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
